import { useRef, useState } from 'react';
import { sistemaDe, validadeDe, validarQuantidade, type Produto } from '@themis/shared';

interface Props {
  produto: Produto;
  onCancelar: () => void;
  onSalvar: (quantidade: number, validade: string) => Promise<boolean>;
  /** Só admin/master recebe; abre a edição de cadastro. */
  onEditar?: (() => void) | undefined;
  /** Estoque travado: mostra o motivo em vez de deixar o usuário tentar e falhar. */
  somenteLeitura?: boolean;
  /** Mostrar saldo do sistema e diferença. Falso para quem só conta. */
  verSistema?: boolean;
}

function marcaDeTempo(p: Produto): number {
  return p.lastModified instanceof Date ? p.lastModified.getTime() : 0;
}

/**
 * Formulário do card expandido.
 *
 * Componente separado de propósito: ele **monta** quando o card abre, então o valor
 * inicial dos campos é lido uma vez, no `useState`. Quando isso era um `useEffect` com
 * `produto` nas dependências, qualquer gravação de outro aparelho trocava a identidade do
 * objeto e apagava o que o funcionário estava digitando.
 */
export function FormContagem({
  produto,
  onCancelar,
  onSalvar,
  onEditar,
  somenteLeitura = false,
  verSistema = false,
}: Props) {
  const [quantidade, setQuantidade] = useState(
    produto.quantidade != null ? String(produto.quantidade) : '',
  );
  const [validade, setValidade] = useState(validadeDe(produto) ?? '');
  const [salvando, setSalvando] = useState(false);
  const [tentou, setTentou] = useState(false);

  // Marca de tempo de quando o formulário abriu. Se o documento mudar enquanto ele está
  // aberto, outro aparelho contou o mesmo item — avisar antes é melhor que deixar o
  // usuário salvar por cima e só então receber o erro de conflito.
  const abertoEm = useRef(marcaDeTempo(produto));
  const mudouNoServidor = marcaDeTempo(produto) > abertoEm.current;

  const validacao = validarQuantidade(quantidade);
  const sistema = sistemaDe(produto);
  const diferenca = validacao.ok ? validacao.valor - sistema : null;

  async function salvar() {
    setTentou(true);
    if (!validacao.ok || salvando) return;
    setSalvando(true);
    try {
      const ok = await onSalvar(validacao.valor, validade);
      if (ok) onCancelar();
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="card__cascata">
      {mudouNoServidor && (
        <p className="aviso" role="alert" style={{ marginBottom: 'var(--e4)' }}>
          Outro aparelho alterou este produto agora. Confira antes de salvar.
        </p>
      )}

      <div className="card__campos">
        <label className="campo">
          <span className="campo__rotulo">Quantidade contada</span>
          <input
            className="campo__entrada campo__entrada--numero"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void salvar();
            }}
            autoFocus
          />
          {/*
            Contagem às cegas: quem só conta não vê o saldo do sistema nem a diferença.
            Ver o número faz conferir em vez de contar, e o inventário deixa de medir
            qualquer coisa. Ver `permissoes.verEstoqueSistema`.
          */}
          <span className="campo__ajuda">
            {verSistema ? (
              <>
                Sistema: {sistema}
                {diferenca !== null && diferenca !== 0 && (
                  <> · diferença {diferenca > 0 ? `+${diferenca}` : diferenca}</>
                )}
              </>
            ) : (
              'Conte o que está na prateleira'
            )}
          </span>
        </label>

        <label className="campo">
          <span className="campo__rotulo">Validade mais curta</span>
          <input
            className="campo__entrada"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
          />
          <span className="campo__ajuda">Opcional</span>
        </label>
      </div>

      {!validacao.ok && (tentou || quantidade.trim() !== '') && (
        <p className="aviso aviso--perigo" role="alert" style={{ marginBottom: 'var(--e4)' }}>
          {validacao.mensagem}
        </p>
      )}

      {somenteLeitura && (
        <p className="aviso" role="status" style={{ marginBottom: 'var(--e4)' }}>
          Este estoque está em modo somente leitura. A contagem está travada.
        </p>
      )}

      <div className="card__acoes">
        {onEditar && (
          <button className="botao botao--secundario" type="button" onClick={onEditar}>
            Editar
          </button>
        )}
        <button className="botao botao--secundario" type="button" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          className="botao botao--primario"
          type="button"
          onClick={() => void salvar()}
          disabled={!validacao.ok || salvando || somenteLeitura}
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
